'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import entries from 'assets/allBuilderPosts.json';

import ListingDeck, { getArrayParam } from 'components/ListingDeck';
import { convertToBuilderIds } from 'components/ListingDeck/utils/convertToBuilderIds';
import generateSidebarItems from 'components/ListingDeck/utils/generateSidebarItems';
import getFilteredPosts from 'components/ListingDeck/utils/getFilteredEntries';
import ListingHeader from 'components/ListingHeader';

import { isNonEmptyArray } from 'utils/functions';

import type { SearchResponse } from 'components/Search/Listing/utils/retrieveSearch';
import type { TemplateModelData } from 'types/types';

const HealthCareInsights = () => {
  const [builderIds, updateBuilderIds] = useState<string[]>([]);
  const [emptySearch, setEmptySearch] = useState<boolean>(true);
  const [builderResults, setBuilderResults] = useState<SearchResponse['results']>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTrigger, setSearchTrigger] = useState(false);

  const posts = entries as TemplateModelData[];

  const paramsRetrieval = useSearchParams();
  const searchParams: Record<string, string | undefined> = {};
  paramsRetrieval.forEach((value, key) => {
    searchParams[key] = value;
  });

  const page = typeof searchParams.page === 'string' ? Number(searchParams.page) : 1;

  const specialties = getArrayParam(searchParams['Specialty']);
  const contentTypes = getArrayParam(searchParams['Content Type']);
  const interests = getArrayParam(searchParams['Subject Matter']);
  const search = paramsRetrieval.get('search') || '';

  useEffect(() => {
    setEmptySearch(!search);

    if (search) {
      const fetchElastic = async (searchTerm: string) => {
        setLoading(true);
        const response = await fetch(`/api/elastic?search=${searchTerm}&recency=true`);
        const data = (await response.json()) as SearchResponse;
        const fetchedResults = data?.results ?? [];
        setLoading(false);
        const fetchedIds = data?.results?.map(result => result.builderId);

        if (isNonEmptyArray(fetchedIds)) {
          updateBuilderIds(fetchedIds);
          setBuilderResults(fetchedResults);
        } else {
          updateBuilderIds([]);
          setBuilderResults([]);
        }
      };

      fetchElastic(search);
    }

    return updateBuilderIds([]);
  }, [search, searchTrigger]);

  const convertedResources = convertToBuilderIds(contentTypes);
  const filteredItems = getFilteredPosts(builderIds, specialties, convertedResources, interests, posts, emptySearch);
  const filteredPostIds = new Set(filteredItems.map(post => post.id));
  const searchBarItems = builderResults.filter(result => filteredPostIds.has(result.builderId));

  const sidebarItems = generateSidebarItems(posts);
  const orderedFilteredItems =
    builderIds.length > 0
      ? filteredItems.sort((a, b) => builderIds.indexOf(a.id || '') - builderIds.indexOf(b.id || ''))
      : filteredItems;

  return (
    <>
      <ListingHeader
        loading={loading}
        filteredItems={searchBarItems}
        search={search || ''}
        onSearchTrigger={() => setSearchTrigger(prev => !prev)}
      />
      <ListingDeck
        loading={loading}
        sidebarItems={sidebarItems}
        orderedFilteredItems={orderedFilteredItems}
        page={page}
        search={search || ''}
      />
    </>
  );
};

export default HealthCareInsights;
